import { useState, useEffect } from 'react'
import { useTasks } from '../context/TaskContext'
import { formatDateTime } from '../utils/dateUtils'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, 
  Sparkles, 
  Calendar, 
  Clock, 
  Tag, 
  AlignLeft, 
  Flag,
  Check,
  Loader2,
  Type
} from 'lucide-react'

function TaskModal({ isOpen, onClose, task = null }) {
  const { createTask, updateTask, parseNaturalLanguage } = useTasks()
  const [isLoading, setIsLoading] = useState(false)
  const [useNaturalLanguage, setUseNaturalLanguage] = useState(false)
  const [naturalInput, setNaturalInput] = useState('')
  const [parsedPreview, setParsedPreview] = useState(null)
  const [activeTab, setActiveTab] = useState('details')
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general',
    dueDate: '',
    dueTime: '',
    durationMinutes: 30,
    tags: [],
  })

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (task) {
        setFormData({
          title: task.title || '',
          description: task.description || '',
          priority: task.priority || 'medium',
          category: task.category || 'general',
          dueDate: task.dueDate || '',
          dueTime: task.dueTime || '',
          durationMinutes: task.durationMinutes || 30,
          tags: task.tags || [],
        })
        setUseNaturalLanguage(false)
        setActiveTab('details')
      } else {
        setFormData({
          title: '',
          description: '',
          priority: 'medium',
          category: 'general',
          dueDate: '',
          dueTime: '',
          durationMinutes: 30,
          tags: [],
        })
        setNaturalInput('')
        setParsedPreview(null)
        setActiveTab('quick')
      }
    }
  }, [isOpen, task])

  // Parse natural language input
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (useNaturalLanguage && naturalInput.trim()) {
        const parsed = await parseNaturalLanguage(naturalInput)
        setParsedPreview(parsed)
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [naturalInput, useNaturalLanguage, parseNaturalLanguage])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      if (activeTab === 'quick' && naturalInput.trim() && !task) {
        await createTask({ naturalLanguage: naturalInput })
      } else if (task) {
        await updateTask(task.id, formData)
      } else {
        await createTask(formData)
      }
      onClose()
    } catch (error) {
      console.error('Error saving task:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95, 
      y: 20,
      transition: { duration: 0.2 }
    }
  }

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  }

  const tabContentVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 10 }
  }

  if (!isOpen) return null

  return (
    <motion.div
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-indigo-900/20 dark:to-purple-900/20">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              task 
                ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' 
                : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
            }`}>
              {task ? <Check className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {task ? 'Edit Task' : 'Create New Task'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {task ? 'Update your task details' : 'Add a new task to your list'}
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </motion.button>
        </div>

        {/* Tabs - Only show for new tasks */}
        {!task && (
          <div className="flex border-b border-gray-100 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('quick')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                activeTab === 'quick'
                  ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/20'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Quick Add
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                activeTab === 'details'
                  ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/20'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Type className="w-4 h-4" />
              Detailed
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
            <AnimatePresence mode="wait">
              {activeTab === 'quick' && !task ? (
                <motion.div
                  key="quick"
                  variants={tabContentVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-indigo-500" />
                      What do you need to do?
                    </label>
                    <input
                      type="text"
                      value={naturalInput}
                      onChange={(e) => setNaturalInput(e.target.value)}
                      placeholder="e.g., Meeting with team tomorrow at 2pm for 1 hour"
                      className="form-input text-lg"
                      autoFocus
                    />
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Try: &quot;Call mom in 30 minutes&quot; or &quot;Submit report by Friday 5pm high priority&quot;
                    </p>
                  </div>

                  <AnimatePresence>
                    {parsedPreview && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: 'auto' }}
                        exit={{ opacity: 0, y: -10, height: 0 }}
                        className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl border border-indigo-100 dark:border-indigo-800"
                      >
                        <p className="text-sm font-medium text-indigo-900 dark:text-indigo-300 mb-2 flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Parsed Preview
                        </p>
                        <div className="space-y-1 text-sm text-indigo-800 dark:text-indigo-200">
                          <p className="font-semibold">{parsedPreview.title}</p>
                          {parsedPreview.dueDate && (
                            <p className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5" />
                              {formatDateTime(parsedPreview.dueDate, parsedPreview.dueTime)}
                            </p>
                          )}
                          {parsedPreview.priority !== 'medium' && (
                            <p className="flex items-center gap-1.5">
                              <Flag className="w-3.5 h-3.5" />
                              {parsedPreview.priority} priority
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ) : (
                <motion.div
                  key="details"
                  variants={tabContentVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.2 }}
                  className="space-y-5"
                >
                  {/* Title */}
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Type className="w-4 h-4 text-indigo-500" />
                      Task Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="What needs to be done?"
                      className="form-input"
                      required
                      autoFocus
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <AlignLeft className="w-4 h-4 text-indigo-500" />
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Add details about this task..."
                      className="form-textarea"
                      rows={3}
                    />
                  </div>

                  {/* Priority & Category */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label flex items-center gap-2">
                        <Flag className="w-4 h-4 text-indigo-500" />
                        Priority
                      </label>
                      <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="low">🟢 Low</option>
                        <option value="medium">🟡 Medium</option>
                        <option value="high">🔴 High</option>
                      </select>
                    </div>

                    <div>
                      <label className="form-label flex items-center gap-2">
                        <Tag className="w-4 h-4 text-indigo-500" />
                        Category
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="general">📋 General</option>
                        <option value="work">💼 Work</option>
                        <option value="personal">🏠 Personal</option>
                        <option value="health">💪 Health</option>
                        <option value="shopping">🛒 Shopping</option>
                      </select>
                    </div>
                  </div>

                  {/* Due Date & Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-indigo-500" />
                        Due Date
                      </label>
                      <input
                        type="date"
                        name="dueDate"
                        value={formData.dueDate}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>

                    <div>
                      <label className="form-label flex items-center gap-2">
                        <Clock className="w-4 h-4 text-indigo-500" />
                        Due Time
                      </label>
                      <input
                        type="time"
                        name="dueTime"
                        value={formData.dueTime}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Clock className="w-4 h-4 text-indigo-500" />
                      Duration (minutes)
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        name="durationMinutes"
                        value={formData.durationMinutes}
                        onChange={handleChange}
                        min={5}
                        max={240}
                        step={5}
                        className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                      <span className="w-16 text-center font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-lg">
                        {formData.durationMinutes}m
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isLoading || (activeTab === 'quick' && !task && !naturalInput.trim()) || (activeTab === 'details' && !formData.title.trim())}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {task ? 'Update Task' : 'Create Task'}
                </>
              )}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}

export default TaskModal
